import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';

import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {clearNavigationWarnings} from 'lib/navigateToURL';
import MethodSelectDropdown from 'procedures/design/MethodSelectDropdown';
import ProcedureNameEditor from 'procedures/design/ProcedureNameEditor';
import {
  trackSwitchedMethodConfiguration,
  trackToggledProcedureViewEvent,
} from 'procedures/procedureAnalyticsEventsUtil';
import {MethodNode} from 'procedures/ProcedureDomainTypes';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import {ellipsisText} from 'styles/Utilities';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, GRAY_LIGHT, RED, SILVER, WHITE} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import {SaveState, SaveStatusIndicator} from 'ui-library/SaveStatusIndicator';

import ProcedureHeaderActionButtons from './ProcedureHeaderActionButtons';
import {PROCEDURE_HEADER_HEIGHT} from './procedureMethodNodeCommonConsts';
import ProcedureTemplateCollectibleBadge from './ProcedureTemplateCollectibleBadge';
import useUnsavedTabChangesCheck from './useUnsavedTabChangesCheck';

interface LegacyProcedureEditorHeaderProps {
  methodNodes: Array<MethodNode>;
  onRename: (newName: string) => Promise<void>;
  onAddMethod: () => void;
  onSwitchVersionId: (versionId: string) => void;
  isProcedureReadOnly: boolean;
}

export default function LegacyProcedureEditorHeader(props: LegacyProcedureEditorHeaderProps): JSX.Element {
  const {methodNodes} = props;
  const {
    legacyIsProcedureDirty,
    legacyIsSaving,
    selectedMethodCid,
    setSelectedMethodCid,
    isEditingMethodTab,
    procedureMetadata,
  } = ProcedurePageContext.useContext();
  const procedureId = procedureMetadata.id;
  const procedureType = procedureMetadata.procedureType;
  const runUnsavedDefinitionsChangesCheck = useUnsavedTabChangesCheck();

  const selectedMethod =
    selectedMethodCid != null ? methodNodes.find((methodNode) => methodNode.cid === selectedMethodCid) : null;

  const onChangeMethodSelection = useCallback(
    (newSelectedMethodCid: string | null) => {
      const selectMethodCidCallback = (): void => {
        setSelectedMethodCid(newSelectedMethodCid);
        const newSelectedMethod = methodNodes.find(({cid}) => cid === newSelectedMethodCid);
        assert(newSelectedMethod != null, 'Selected unit operation must exist');
        const {id: methodId, cid: methodCid, nodeId} = newSelectedMethod;
        trackSwitchedMethodConfiguration(procedureId, procedureType, methodId, methodCid, nodeId);
      };
      runUnsavedDefinitionsChangesCheck(selectMethodCidCallback);
    },
    [runUnsavedDefinitionsChangesCheck, setSelectedMethodCid, methodNodes, procedureId, procedureType]
  );

  const onCloseMethodEditor = useCallback((): void => {
    runUnsavedDefinitionsChangesCheck(() => {
      clearNavigationWarnings();
      if (selectedMethodCid != null) {
        // Only track event when we've actually switched back to the flowchart view
        trackToggledProcedureViewEvent(procedureId, procedureType, ActionOrigin.PROCEDURE_DESIGNER_DETAILS);
      }
      setSelectedMethodCid(null);
    });
  }, [
    procedureId,
    runUnsavedDefinitionsChangesCheck,
    selectedMethodCid,
    setSelectedMethodCid,
    procedureType,
  ]);

  const hasUnsavedChanges = legacyIsSaving || legacyIsProcedureDirty;
  const saveStatus = hasUnsavedChanges ? SaveState.SAVING : SaveState.SAVED;

  return (
    <div className={css(styles.headerBar)}>
      <div className={css(styles.leftGroup)}>
        <div className={css(styles.nameAndIconGroup)}>
          <BenchlingIcon className={css(styles.folderIcon)} icon={FolderItemIcon.Recipe} />
          <div className={css(styles.name)}>
            {!props.isProcedureReadOnly ? (
              <ProcedureNameEditor
                name={procedureMetadata.name}
                onRename={props.onRename}
                disableEditsWithTooltip={
                  isEditingMethodTab ? "Finish editing unit operation's definitions" : null
                }
              />
            ) : (
              // TODO: BNCH-90283 tooltip should only be enabled when name overflows, and should not scroll
              <Tooltip text={procedureMetadata.name} placement={FloatingPlacement.BOTTOM_START}>
                <div className={css(styles.archivedProcedureNameAndLabel)}>
                  <div className={css(styles.name)}>
                    {procedureMetadata.name}
                    {isArchived(procedureMetadata) && (
                      <ArchiveRecordLabel
                        item={{
                          id: procedureId,
                          archiveData: procedureMetadata.archiveData,
                          name: procedureMetadata.name,
                        }}
                      />
                    )}
                  </div>
                </div>
              </Tooltip>
            )}
          </div>
          {BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE && <ProcedureTemplateCollectibleBadge />}
          {!props.isProcedureReadOnly && (
            <SaveStatusIndicator
              saveStatus={saveStatus}
              dataTestAttrs={{component: 'LegacyProcedureEditorHeader', element: 'saveStateIndicator'}}
            />
          )}
        </div>
        <div className={css(styles.headerNameDisplay)}>
          <Button
            onClick={onCloseMethodEditor}
            buttonStyle={ButtonStyle.TRANSPARENT}
            size={ButtonSize.MEDIUM}
            className={css(styles.flowchartButton)}
            text="Flowchart"
            dataTestAttrs={{component: 'LegacyProcedureEditorHeader', element: 'flowchartButton'}}
          />
          <BenchlingIcon icon="chevron" className={css(styles.chevronIcon)} />
          {selectedMethod && (
            <>
              <div className={css(styles.edit)}>Edit</div>
              <MethodSelectDropdown
                selectedMethod={selectedMethod}
                methodNodes={methodNodes}
                onChangeMethodSelection={onChangeMethodSelection}
                onAddMethod={props.onAddMethod}
              />
            </>
          )}
        </div>
      </div>
      <ProcedureHeaderActionButtons
        locked={props.isProcedureReadOnly}
        hasUnsavedChanges={hasUnsavedChanges}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  headerBar: {
    background: WHITE,
    marginBottom: '4px',
    boxShadow: `0px 2px 4px ${GRAY_LIGHT}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexGrow: 1,
    height: `${PROCEDURE_HEADER_HEIGHT}px`,
    minHeight: `${PROCEDURE_HEADER_HEIGHT}px`,
    paddingTop: '6px',
    overflow: 'hidden',
  },
  button: {
    marginLeft: 5,
    marginRight: 5,
  },
  nameAndIconGroup: {
    display: 'flex',
    gap: '10px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    alignItems: 'center',
    height: '36px',
  },
  name: {
    fontSize: '16px',
    padding: '6px',
    minWidth: '100px',
    ...ellipsisText,
  },
  archivedProcedureNameAndLabel: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  folderIcon: {
    height: 18,
    color: SILVER,
  },
  chevronIcon: {
    height: 12,
    marginRight: 10,
    color: SILVER,
  },
  saveStateIndicator: {
    display: 'flex',
    alignItems: 'center',
    color: GRAY,
    marginRight: '4px',
  },
  validationErrorIcon: {
    color: RED,
    width: '15px',
    height: '15px',
    marginRight: '8px',
    display: 'flex',
  },
  leftGroup: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: '16px',
    paddingRight: '16px',
    overflow: 'hidden',
  },
  headerNameDisplay: {
    display: 'flex',
    height: '32px',
    fontSize: '14px',
    fontWeight: 400,
    alignItems: 'center',
    marginBottom: '4px',
  },
  edit: {
    padding: '0px 8px 1px 0px',
  },
  flowchartButton: {
    padding: '5px 4px 7px 4px',
    marginRight: '6px',
    borderRadius: '2px',
    height: 'auto',
    fontWeight: 400,
    fontSize: '14px',
    ':hover': {
      background: Color.GRAY_100,
    },
  },
  templateBadge: {
    marginLeft: '-15px',
  },
});
